import { CardContainer } from "@msys/ui";
import { Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, type FormikProps } from "formik";
import { ManualSave } from "../../../../commons/form-fields/ManualSave.js";
import { TextField } from "../../../../commons/form-fields/TextField.js";
import { useUnsavedChangesGuard } from "../../../../commons/hooks/useUnsavedChangesGuard.js";
import type { IntegrationSettingsProps } from "../../integrationRegistry.js";
import { getIntegrationMeta } from "../../integrationRegistry.js";
import { SettingsPageLayout } from "./SettingsPageLayout.js";
import { useIntegrationSettingsBase } from "./useIntegrationSettingsBase.js";
import { integrationErrorMessage } from "../../integrationErrorMessage.js";
import {
  CardSaveRow,
  FormDirtyTracker,
  useCardDirtyRegistry,
} from "./integrationCardForm.js";

const MONO_INPUT_SX = {
  "& .MuiInputBase-input": { fontFamily: "monospace", fontSize: "0.875rem" },
};

interface FormValues {
  supplierId: string;
  supplierCatalogueId: string;
  configurableProductSupplierCatalogueId: string;
  assetsNamespaceKey: string;
}

// One card for the whole page: four related fields don't warrant three
// separately saved cards — the former Supplier/Catalogues/Assets cards
// became subsections
const ConfigurationSection = ({ fp }: { fp?: FormikProps<FormValues> }) => {
  const { t } = useTranslate("Integrations");
  return (
    <CardContainer title={t("Configuration")}>
      <Stack spacing={1} sx={{ px: 1, pt: 0.5, pb: 1 }}>
        <Typography variant="subtitle2">{t("Supplier")}</Typography>
        <TextField
          name="supplierId"
          label={t("Supplier ID")}
          sx={MONO_INPUT_SX}
        />

        <Typography variant="subtitle2" sx={{ pt: 1 }}>
          {t("Catalogues")}
        </Typography>
        <TextField
          name="supplierCatalogueId"
          label={t("Supplier Catalogue ID")}
          sx={MONO_INPUT_SX}
        />
        <TextField
          name="configurableProductSupplierCatalogueId"
          label={t("Configurable Product Catalogue ID")}
          sx={MONO_INPUT_SX}
        />

        <Typography variant="subtitle2" sx={{ pt: 1 }}>
          {t("Assets")}
        </Typography>
        <TextField
          name="assetsNamespaceKey"
          label={t("Assets Namespace Key")}
          sx={MONO_INPUT_SX}
        />

        {fp && <CardSaveRow fp={fp} />}
      </Stack>
    </CardContainer>
  );
};

export const PimProductsSettings = ({
  integrationId,
  mode,
}: IntegrationSettingsProps) => {
  const { t } = useTranslate("Integrations");
  const {
    orgIntegration,
    refetchDashboard,
    loading,
    removeLoading,
    handleAdd,
    handleUpdate,
    handleDisconnect,
    enqueueSnackbar,
  } = useIntegrationSettingsBase(integrationId, mode);
  const { anyDirty, dirtyOf } = useCardDirtyRegistry();

  useUnsavedChangesGuard(
    anyDirty,
    t("You have unsaved changes. Leave the page anyway?")
  );

  const integrationName =
    getIntegrationMeta(integrationId).sidebarLabel ?? "PIM Products";
  const settings = (orgIntegration?.settings as any) ?? {};

  const isCreate = mode === "create";

  const initialValues: FormValues = {
    supplierId: settings.supplierId ?? "",
    supplierCatalogueId: settings.supplierCatalogueId ?? "",
    configurableProductSupplierCatalogueId:
      settings.configurableProductSupplierCatalogueId ?? "",
    assetsNamespaceKey: settings.assetsNamespaceKey ?? "",
  };

  const handleSave = async (values: FormValues) => {
    try {
      await handleUpdate(values);
      // handleUpdate doesn't refetch — fetch the new stored settings so
      // enableReinitialize resets the card
      await refetchDashboard();
    } catch (e) {
      enqueueSnackbar(
        integrationErrorMessage(
          e,
          t("Settings could not be saved. Please check your input.")
        ),
        { variant: "error" }
      );
    }
  };

  return (
    <SettingsPageLayout
      integrationId={integrationId}
      integrationName={integrationName}
      title={
        isCreate
          ? `${t("Connect")} PIM Products`
          : `${integrationName} — ${t("Settings")}`
      }
      onDisconnect={isCreate ? undefined : handleDisconnect}
      events={isCreate ? undefined : (orgIntegration?.events ?? [])}
      onRefreshEvents={refetchDashboard}
      hasDashboard={!isCreate && !!orgIntegration?.health}
      removeLoading={removeLoading}
      loading={!isCreate && loading && !orgIntegration}
    >
      {isCreate ? (
        <Formik<FormValues>
          initialValues={initialValues}
          onSubmit={async values => {
            try {
              await handleAdd(values);
            } catch (e) {
              enqueueSnackbar(
                integrationErrorMessage(
                  e,
                  t("Settings could not be saved. Please check your input.")
                ),
                { variant: "error" }
              );
            }
          }}
          enableReinitialize
        >
          {formikProps => (
            <Form>
              <Stack spacing={2}>
                <ConfigurationSection />
                <ManualSave
                  onCancel={formikProps.handleReset}
                  disabled={formikProps.isSubmitting}
                  disabledCancel={!formikProps.dirty}
                  saveButtonTitle={t("Connect")}
                />
              </Stack>
            </Form>
          )}
        </Formik>
      ) : (
        <Formik<FormValues>
          initialValues={initialValues}
          onSubmit={handleSave}
          enableReinitialize
        >
          {fp => (
            <Form>
              <FormDirtyTracker
                onDirtyChange={dirtyOf("configuration")}
                dirty={fp.dirty}
              />
              <ConfigurationSection fp={fp} />
            </Form>
          )}
        </Formik>
      )}
    </SettingsPageLayout>
  );
};
